<template>
    <div class="product-card">
        <div class="product-card__preview">
            <img :src="preview" :class="previewSize" />
            <el-button
                v-if="shouldShowDelete"
                class="product-card__delete"
                size="mini"
                type="danger"
                icon="mdi mdi-delete"
                @click="deleteItem()"
            />
        </div>

        <div class="product-card__content">
            <div class="product-card__main-content">
                <div class="product-card__title">
                    <h5 v-html="title"></h5>
                    <img
                        v-show="isNew"
                        src="/images/new.png"
                        alt="new"
                        class="image-new"
                    />
                </div>

                <template v-if="orderItemSize && showPrice">
                    <div class="product-card__details">
                        <div class="product-card__quantity">
                            <label>Quantity</label>
                            <el-select
                                v-model.number="form.quantity"
                                @change="onChangeQuantity"
                                v-validate.disable="'required'"
                                placeholder="Select"
                            >
                                <el-option
                                    v-for="item in quantityOptions"
                                    :key="item"
                                    :label="item"
                                    :value="item"
                                />
                            </el-select>
                        </div>
                        <div class="product-card__price">
                            <label>Price</label>
                            <span>{{ orderItemSize.amount | currency }}</span>
                        </div>
                    </div>
                </template>
                <template v-else-if="orderItemDigitalFile && showPrice">
                    <div class="product-card__details">
                        <div class="product-card__quantity">
                            <label>Quantity</label>
                            <span>1</span>
                            <!-- Fixed quantity -->
                        </div>
                        <div class="product-card__price">
                            <label>Price</label>
                            <span>{{
                                orderItemDigitalFile.price | currency
                            }}</span>
                        </div>
                    </div>
                </template>
                <template v-else-if="orderItemPrintedExtra && showPrice">
                    <div class="product-card__details">
                        <div class="product-card__quantity">
                            <label>Quantity</label>
                            <el-input-number
                                :min="1"
                                :step="1"
                                @change="onChangeQuantity"
                                controls-position="right"
                                name="quantity"
                                placeholder="Quantity"
                                style="width: 100%; max-width: 200px"
                                v-model.number="form.quantity"
                                v-validate.disable="'required|min_value:1'"
                            />
                        </div>
                        <div class="product-card__price">
                            <label>Price</label>
                            <span>
                                {{
                                    (orderItemPrintedExtra.price *
                                        orderItemPrintedExtra.quantity)
                                        | currency
                                }}</span
                            >
                        </div>
                    </div>
                </template>
                <template v-if="!showPrice">
                    <template>
                        <el-form-item
                            :class="form.state('quantity')"
                            class="mb-0"
                            v-if="requiresQuantity"
                        >
                            <el-select
                                v-if="shouldShowQuantityDropdown"
                                v-model.number="form.quantity"
                                @change="onChangeQuantity"
                                v-validate.disable="'required'"
                                placeholder="Select"
                            >
                                <el-option
                                    v-for="item in quantityOptions"
                                    :key="item"
                                    :label="item"
                                    :value="item"
                                >
                                </el-option>
                            </el-select>
                            <el-input-number
                                placeholder="Quantity"
                                v-else
                                v-model.number="form.quantity"
                                @change="onChangeQuantity"
                                style="max-width: 200px"
                                :min="0"
                                class="mt-5"
                                name="quantity"
                                v-validate.disable="'required|min_value:1'"
                                :step="1"
                            />
                            <div
                                class="el-form-item__error"
                                v-if="form.errors.has('quantity')"
                            >
                                {{ form.errors.get("quantity") }}
                            </div>
                            <form-feedback :form="form" field="name" />
                        </el-form-item>
                        <div v-if="requiresPaperColor">
                            <div class="text-left">Paper Color:</div>
                            <color-select
                                :best-judgement-enabled="true"
                                :predefine="colors"
                                @best-judgement-changed="onChangeBestJudgement"
                                @input="onChangePaperColor"
                                v-model="form.paper_color"
                            />
                        </div>
                    </template>
                </template>
            </div>

            <div class="product-card__actions">
                <div v-if="addedToCart" class="text-center success-text">
                    <i class="mdi mdi-check" /> Added to Cart
                </div>
                <el-button
                    v-else
                    :loading="loading"
                    size="mini"
                    :type="addedToCart ? 'success' : 'primary'"
                    round
                    class="mt-20"
                    :icon="addedToCart ? 'mdi mdi-check' : 'mdi mdi-cart-plus'"
                    @click="addToCart()"
                >
                    <span v-if="addedToCart">Added to Cart</span>
                    <span v-else>Add to Cart</span>
                </el-button>
            </div>
        </div>
    </div>
</template>

<script>
import ColorSelect from "@/components/ColorSelect";
import * as CalendarFormats from "@/constants/calendar-formats";
import * as ColorTypes from "@/constants/color-types";
import * as SizeTypes from "@/constants/size-types";

export default {
    props: {
        size: {
            type: Object,
            required: false
        },
        calendarFormat: {
            type: Number,
            required: false,
            default: null
        },
        digitalFile: {
            type: Object,
            required: false
        },
        printedExtra: {
            type: Object,
            required: false
        },
        colorType: {
            type: String,
            default: ColorTypes.COLORED
        },
        requiresPaperColor: {
            type: Boolean,
            default: false
        },
        requiresQuantity: {
            type: Boolean,
            default: true
        },
        loading: {
            type: Boolean,
            default: false
        },
        addedToCart: {
            type: Boolean,
            default: false
        },
        customTitle: {
            type: String
        },
        orderItemSize: {
            type: Object
        },
        orderItemDigitalFile: {
            type: Object
        },
        orderItemPrintedExtra: {
            type: Object
        },
        showPrice: {
            type: Boolean,
            default: false
        },
        previewSize: {
            type: String,
            default: "w-full"
        }
    },
    components: {
        ColorSelect
    },
    data() {
        return {
            form: new Form({
                quantity: null,
                paper_color: null,
                best_judgement: false,
                price: null
            })
        };
    },
    mounted() {
        this.form.setVeeErrors(this.veeErrors);
        if (this.orderItemSize) {
            this.form.quantity = this.orderItemSize.quantity;
            this.form.price = this.orderItemSize.amount;
            this.form.paper_color = this.orderItemSize.paper_color;
        } else if (this.orderItemPrintedExtra) {
            //let orderItemPrintedExtra = this.orderItemPrintedExtra;
            // console.log(
            //     "mounted:orderItemPrintedExtra",
            //     this.orderItemPrintedExtra
            // );
            this.form.quantity = this.orderItemPrintedExtra.quantity;
        } else {
            if (this.requiresPaperColor) {
                this.form.paper_color = "#FFFFFF";
            }
        }
    },
    watch: {
        "orderItemPrintedExtra.quantity"(value) {
            //console.log('watch:orderItemPrintedExtra.quantity', {value});
            this.form.quantity = value;
        },
        "orderItemSize.quantity"(value) {
            this.form.quantity = value;
        },
        "orderItemSize.paper_color"(value) {
            this.form.paper_color = value;
        }
    },
    computed: {
        colors() {
            return this.$store.state.config.settings.paper_colors;
        },
        preview() {
            if (this.printedExtra) {
                return this.printedExtra.image.url;
            } else if (this.digitalFile) {
                return this.digitalFile.image.url;
            } else {
                if (this.calendarFormat === CalendarFormats.WEEKLY) {
                    return this.size.preview_weekly_calendar;
                }
                if (this.colorType === ColorTypes.COLORED) {
                    return this.size.preview;
                }
                return this.size.preview_bw;
            }
        },
        name() {
            if (this.printedExtra) {
                return this.printedExtra.name;
            } else if (this.digitalFile) {
                return this.digitalFile.name;
            }
            return this.size.name;
        },
        title() {
            if (this.customTitle) {
                return this.customTitle;
            }
            if (this.printedExtra) {
                return this.printedExtra.name.replace(":", ":<br />");
            } else if (this.digitalFile) {
                return this.digitalFile.name;
            }
            return `Quantity of ${this.size.name} ${
                this.colorType === ColorTypes.COLORED ? "color" : "black ink"
            } ${this.size.type === SizeTypes.FLAT ? "copies" : "posters"}`;
        },
        quantityOptions() {
            if (!this.size) {
                return [];
            }
            switch (this.size.type) {
                case SizeTypes.FLAT:
                    return this.$store.state.config.settings.pricing.quantities
                        .calendars.flats;
                case SizeTypes.POSTER:
                    return this.$store.state.config.settings.pricing.quantities
                        .calendars.posters;
            }
            return [];
        },
        shouldShowQuantityDropdown() {
            return !!this.size;
        },
        shouldShowDelete() {
            return (
                this.orderItemSize ||
                this.orderItemDigitalFile ||
                this.orderItemPrintedExtra
            );
        },
        isNew() {
            if (this.printedExtra && this.printedExtra.is_new) {
                return true;
            } else {
                return false;
            }
        },
        price() {
            if (this.printedExtra) {
                return this.printedExtra.price;
            }
            return null;
        }
    },
    methods: {
        onChangeBestJudgement(best_judgement) {
            this.form.best_judgement = best_judgement;
        },
        addToCart() {
            if (this.addedToCart) {
                return;
            }
            this.$validator.validate().then((valid) => {
                if (!valid) {
                    return false;
                }
                this.$emit("add-to-cart", {
                    ..._.pick(this.form, [
                        "quantity",
                        "paper_color",
                        "best_judgement"
                    ]),
                    size: this.size,
                    digital_file: this.digitalFile,
                    printed_extra: this.printedExtra,
                    color_type: this.colorType,
                    component: this
                });
            });
        },
        reset() {
            this.form.quantity = null;
        },
        deleteItem() {
            this.$emit("delete");
        },
        onChangeQuantity(quantity) {
            //console.log('onChangeQuantity', {quantity});
            this.$emit("change-quantity", quantity);
        },
        onChangePaperColor(color) {
            this.$emit("change-paper-color", color);
        }
    }
};
</script>

<style lang="scss" scoped>
@import "../../../../../../assets/scss/product-card.scss";

// Add these styles to ensure consistent heights
.product-card {
  display: flex;
  flex-direction: column;
  height: 100%;

  &__content {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    justify-content: space-between; // This pushes content to the top and actions to the bottom
  }

  // Create a wrapper for the main content to allow it to grow
  &__main-content {
    flex-grow: 1;
  }

  // Add spacing before action buttons
  &__actions {
    margin-top: auto; // Push to bottom
    padding-top: 15px;
  }

  // Preserve existing styles
  .image-new {
    max-height: 40px;
    max-width: 100%;
    filter: drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.3));
  }
}

// Override element-ui select width
:deep(.el-select) {
  width: 100%;
}
</style>
